import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UserAvatar from "@/components/app/UserAvatar";
import { AlertTriangle, Clock, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Visible to GM / Super Admin / Property Admin / Supervisor — surfaces tickets
 * that have blown past their committed closing time, so management can act.
 * Each row offers a one-click "Issue warning" to the assignee (reason pre-filled).
 */
export default function OverdueTicketsRecommendation() {
  const { user } = useAuth();
  const canSee = ["super_admin", "general_manager", "property_admin", "supervisor"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [warnFor, setWarnFor] = useState(null); // overdue row
  const [severity, setSeverity] = useState("soft");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!canSee) return;
    api.get("/tickets/overdue").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  }, [canSee]);

  if (!canSee || !rows || rows.length === 0) return null;

  const openWarn = (row) => {
    setWarnFor(row);
    setSeverity("soft");
    setReason(row.reason_suggestion || "");
  };

  const submitWarn = async () => {
    if (!warnFor?.assignee?.id) { toast.error("This ticket has no assignee to warn"); return; }
    if (reason.trim().length < 3) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${warnFor.assignee.id}/warnings`, { severity, reason: reason.trim() });
      toast.success(`${severity} warning issued to ${warnFor.assignee.name}`);
      setRows((prev) => (prev || []).filter((r) => r.ticket_id !== warnFor.ticket_id));
      setWarnFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue warning");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border-2 border-red-200 rounded-md p-4 sm:p-5" data-testid="overdue-tickets-recommendation">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 text-red-600" />
        <h3 className="font-display text-base sm:text-lg font-bold tracking-tight text-red-700">Overdue tickets — consider a warning</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-red-400">{rows.length} overdue</span>
      </div>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.ticket_id} className="flex items-center gap-3 p-2 rounded-md bg-red-50/50 border border-red-100" data-testid={`overdue-row-${r.ticket_id}`}>
            {r.assignee ? <UserAvatar user={r.assignee} size={36} /> : <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-400"><AlertTriangle className="w-4 h-4" /></div>}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">
                {r.category}{r.room_no ? <span className="text-zinc-500 font-normal"> · Room {r.room_no}</span> : ""}
              </div>
              <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3">
                <span>{r.assignee?.name || "Unassigned"}</span>
                {r.property_name && <span>{r.property_name}</span>}
                <span className="text-red-600 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.overdue_hours}h overdue</span>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 shrink-0"
              disabled={!r.assignee}
              onClick={() => openWarn(r)}
              data-testid={`overdue-warn-${r.ticket_id}`}
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Issue warning
            </Button>
          </li>
        ))}
      </ul>

      <Dialog open={!!warnFor} onOpenChange={(o) => !o && setWarnFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Warn {warnFor?.assignee?.name}</DialogTitle>
            <DialogDescription>This ticket missed its committed closing time. Issue a warning with a documented reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="overdue-warn-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="soft">Soft (orange)</SelectItem>
                  <SelectItem value="hard">Hard (red)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="overdue-warn-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarnFor(null)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitWarn}
              disabled={busy || reason.trim().length < 3}
              className={severity === "hard" ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}
              data-testid="overdue-warn-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Issue {severity} warning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
